import { Button, Typography, notification } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LockOutlined } from '@ant-design/icons';
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Campaign } from "models/CommonType";
import { AppContext } from "context/AppContext";
import PrivateCampaignJoinModal from "./PrivateCampaignJoinModal";

const { Title } = Typography;

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  max-width: 1600px;
  padding: var(--spacing-4xl) var(--spacing-3xl) var(--spacing-lg);
  background: var(--color-bg-card);
  border-top: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-heavy);
`;

const PublicJoinButton = styled(Button)`
  width: 100%;
  height: 48px;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-md);
  color: var(--color-white);
  background: var(--gradient-primary);
  border: none;
  font-size: 16px;
  
  &:hover {
    background: var(--color-primary-dark);
    box-shadow: var(--shadow-primary-hover);
  }
`;

const PrivateJoinButton = styled(Button)`
  width: 100%;
  height: 48px;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-md);
  color: var(--color-white) !important;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%) !important;
  border: none !important;
  font-size: 16px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25), 
              0 8px 25px rgba(0, 0, 0, 0.15),
              0 4px 15px rgba(0, 0, 0, 0.12),
              0 2px 10px rgba(0, 0, 0, 0.1),
              inset 0 2px 0 rgba(255, 255, 255, 0.25),
              inset 0 -1px 0 rgba(0, 0, 0, 0.1),
              0 0 0 2px rgba(255, 255, 255, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
                transparent, 
                rgba(255, 255, 255, 0.25), 
                transparent);
    transition: left 0.6s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    border-radius: var(--radius-md);
    z-index: -1;
    filter: blur(8px);
          filter: blur(18px);
      opacity: 1;
  }
  
  &:hover, &:focus {
    color: var(--color-white) !important;
    background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%) !important;
    border: none !important;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3), 
                0 12px 35px rgba(0, 0, 0, 0.2),
                0 8px 25px rgba(0, 0, 0, 0.15),
                0 4px 15px rgba(0, 0, 0, 0.12),
                inset 0 3px 0 rgba(255, 255, 255, 0.35),
                inset 0 -2px 0 rgba(0, 0, 0, 0.15),
                0 0 0 3px rgba(255, 255, 255, 0.12);
    transform: translateY(-2px);
    
    &::before {
      left: 100%;
    }
    
    &::after {
      opacity: 0.8;
      filter: blur(12px);
    }
  }
  
  &:active {
    color: var(--color-white) !important;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%) !important;
    border: none !important;
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2), 
                0 6px 20px rgba(0, 0, 0, 0.15),
                0 4px 15px rgba(0, 0, 0, 0.12),
                0 2px 10px rgba(0, 0, 0, 0.1),
                inset 0 2px 0 rgba(255, 255, 255, 0.2),
                inset 0 -1px 0 rgba(0, 0, 0, 0.08),
                0 0 0 2px rgba(255, 255, 255, 0.06);
  }
  
  /* Force styles for all states including after modal close */
  &.ant-btn {
    color: var(--color-white) !important;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%) !important;
    border: none !important;
  }
  
  &.ant-btn:hover,
  &.ant-btn:focus,
  &.ant-btn:active {
    color: var(--color-white) !important;
    background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%) !important;
    border: none !important;
  }
  
  .anticon {
    margin-right: var(--spacing-sm);
    color: var(--color-white) !important;
  }
`;

const StatusTitle = styled(Title)`
  padding-bottom: var(--spacing-2xl);
  text-align: center;
  color: var(--color-text-tertiary);
  margin: 0;
`;

interface CampaignJoinBannerProps {
  campaign: Campaign;
  refetchCampaign: () => void;
}

const CampaignJoinBanner: React.FC<CampaignJoinBannerProps> = ({
  campaign,
  refetchCampaign,
}) => {
  const { t } = useTranslation();
  const { api, authentication, modal } = useContext(AppContext);
  const { authKey } = authentication;
  const { openAuthModal } = modal;

  const [isLoading, setIsLoading] = useState(false);
  const [showPrivateModal, setShowPrivateModal] = useState(false);
  const [showPublicConfirmation, setShowPublicConfirmation] = useState(false);

  const handlePublicJoin = async () => {
    if (authKey === '') {
      openAuthModal();
      return;
    }

    if (!campaign.id) return;

    // If there's a fee, show confirmation first
    if (campaign.final_fee && campaign.final_fee > 0) {
      setShowPublicConfirmation(true);
      return;
    }

    // No fee, join directly
    await performJoin();
  };

  const performJoin = async () => {
    if (!campaign.id) return;

    setIsLoading(true);

    try {
      const result = await api.campaign.joinCampaign(campaign.id);

      if (result.error) {
        notification.open({
          message: t('#Campaign.error.join_failed_title'),
          description: result.error.message || t('#Campaign.error.join_failed'),
          icon: <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />,
        });
      } else if (result.is_joined) {
        refetchCampaign();
        notification.open({
          message: t('#Campaign.join_success_title'),
          description: t('#Campaign.join_success'),
          icon: <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />,
        });
      } else {
        notification.open({
          message: t('#Campaign.error.already_joined_title'),
          description: t('#Campaign.error.already_joined'),
          icon: <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />,
        });
      }
    } catch (error: any) {
      let errorMessage = t('#Campaign.error.join_failed');

      if (error && error.detail) {
        errorMessage = error.detail;
      } else if (error?.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].detail || errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notification.open({
        message: t('#Campaign.error.join_failed_title'),
        description: errorMessage,
        icon: <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />,
      });
    }

    setIsLoading(false);
    setShowPublicConfirmation(false);
  };

  const handlePrivateJoin = () => {
    if (authKey === '') {
      openAuthModal();
    } else {
      setShowPrivateModal(true);
    }
  };

  const handlePrivateJoinSubmit = async (code?: string) => {
    if (!campaign.id || !code) return;

    setIsLoading(true);

    try {
      const result = await api.campaign.joinPrivateCampaign(
        campaign.id,
        code,
        campaign.final_fee || 0
      );

      if (result.is_joined) {
        refetchCampaign();
        setShowPrivateModal(false);
        notification.open({
          message: t('#Campaign.join_success_title'),
          description: t('#Campaign.join_success'),
          icon: <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />,
        });
      } else {
        notification.open({
          message: t('#Campaign.error.join_failed_title'),
          description: t('#Campaign.error.join_failed'),
          icon: <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />,
        });
      }
    } catch (error: any) {
      let errorMessage = t('#Campaign.error.join_failed');

      if (error && error.detail) {
        errorMessage = error.detail;
      } else if (error?.response?.data?.errors && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0].detail || errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notification.open({
        message: t('#Campaign.error.join_failed_title'),
        description: errorMessage,
        icon: <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />,
      });
    }

    setIsLoading(false);
  };

  const getJoinButtonText = () => {
    if (campaign.final_fee && campaign.final_fee > 0) {
      return t('#Campaign.join_with_miles', { miles: campaign.final_fee });
    }
    return t('#Campaign.join_now');
  };

  const component = () => {
    const currentDate = Date.now();
    const isFuture = new Date(campaign.started).getTime() > currentDate;
    const isExpired = campaign.expired ? new Date(campaign.expired).getTime() < currentDate : false;

    if (isExpired) {
      return (
        <StatusTitle level={5}>
          {t('#Campaign.expired')}
        </StatusTitle>
      );
    } else if (isFuture) {
      const startedDate = new Date(campaign.started).getTime();
      const diffTime = Math.abs(startedDate - currentDate);
      const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
      if (diffHours > 23) {
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return (
          <StatusTitle level={5}>
            {t('#Campaign.will_start_day', { count: diffDays, day: diffDays })}
          </StatusTitle>
        );
      } else {
        return (
          <StatusTitle level={5}>
            {t('#Campaign.will_start_hour', { count: diffHours, hour: diffHours })}
          </StatusTitle>
        );
      }
    } else {
      // Campaign is active - show appropriate join button
      if (campaign.is_private) {
        return (
          <PrivateJoinButton
            loading={isLoading}
            onClick={handlePrivateJoin}
            icon={<LockOutlined />}
          >
            {t('#Campaign.join_private_campaign')}
          </PrivateJoinButton>
        );
      } else {
        return (
          <PublicJoinButton
            loading={isLoading}
            onClick={handlePublicJoin}
          >
            {getJoinButtonText()}
          </PublicJoinButton>
        );
      }
    }
  };

  const bannerComponent = component();

  return (
    <>
      <div style={{ paddingBottom: 120 }} />
      <Banner>{bannerComponent}</Banner>

      {/* Private Campaign Modal */}
      <PrivateCampaignJoinModal
        visible={showPrivateModal}
        onCancel={() => setShowPrivateModal(false)}
        onJoin={handlePrivateJoinSubmit}
        loading={isLoading}
        finalFee={campaign.final_fee}
        campaignName={campaign.name}
      />

      {/* Public Campaign Confirmation Modal */}
      <PrivateCampaignJoinModal
        visible={showPublicConfirmation}
        onCancel={() => setShowPublicConfirmation(false)}
        onJoin={() => performJoin()}
        loading={isLoading}
        finalFee={campaign.final_fee}
        campaignName={campaign.name}
        isPublicCampaign={true}
      />
    </>
  );
};

export default CampaignJoinBanner;